import { useEffect, useState } from 'react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getSnakeMarketsFindSeriesHistoryQueryKey,
  useSnakeMarketsFindCurrencies,
  useSnakeMarketsFindPastMarket,
  useSnakeMarketsFindSeries,
  usePlaeeOrderBookGetOrderBook,
} from '@/lib/endpoints';
import type { SnakeMarketsFindSeriesSubCategoryCode } from '@/lib/models';
import { Skeleton } from '@/components/ui';
import { toCents } from '@/lib/binary-market';
import { formatSpot, formatWindowEt, isClosed, parsePrice } from '@/lib/snake-market';
import { SnakeCurrencyTabs } from './SnakeCurrencyTabs';
import { SnakeMarketBodySkeleton, SnakeMarketPageSkeleton } from './SnakeMarketPageSkeleton';
import { SnakeMarketHeader } from './SnakeMarketHeader';
import { SnakeTimelineStrip } from './SnakeTimelineStrip';
import { SnakeOpenPositions } from './SnakeOpenPositions';
import { SnakeMyHistory } from './SnakeMyHistory';
import { SnakeSections } from './SnakeSections';
import { SnakeBuyPanel } from './SnakeBuyPanel';
import { SnakeTradeBar } from './SnakeTradeBar';
import { SnakePriceSubscriber } from './SnakePriceSubscriber';
import { useSnakeLive } from './useSnakeLive';
import { useNowMs } from './useNowMs';
import { useSnakePriceToBeatReady } from './useSnakePriceToBeatReady';
import { useTradingRealtimeSync } from '@/lib/trading/useTradingRealtimeSync';
import { useSettlementResync } from '@/lib/trading/useSettlementResync';
import { LightweightChart } from '../LiveTrading/LightweightChart';

/** Build the canonical snake page URL for a currency + resolution. */
function snakeHref(instrument: string, subCategoryCode: string): string {
  return `/snake/${encodeURIComponent(instrument)}/${encodeURIComponent(subCategoryCode)}`;
}

/**
 * Snake market page (ARC-84 §5–6).
 *
 * Orchestrates `/snake/:instrument/:subCategoryCode`: currency + resolution
 * switchers (in place, navigating to the matching URL), the market-state header
 * (price to beat / previous resolution / countdown), the live chart (price line
 * + target/strike line + current-price line + trade dots), the recent-results
 * history strip, the buy/sell panel shell, and the admin-ordered info tabs.
 *
 * Selection defaults: first currency + its closest resolution when the route
 * params are missing/invalid. **Resolution auto-advances** to the next live
 * instance when the current one closes — see {@link useSnakeMarketsFindSeries}
 * refetch below.
 */
export function SnakeMarketPage() {
  const params = useParams<{ instrument?: string; subCategoryCode?: string }>();
  const navigate = useNavigate();
  const nowMs = useNowMs();
  const { live, setPrices, setSpot, addTradeDot } = useSnakeLive();

  const { data: currencyData, isLoading: currenciesLoading } = useSnakeMarketsFindCurrencies();
  const currencies = currencyData?.currencies ?? [];

  // Resolve the active currency + resolution from the route, defaulting to the
  // first currency and its closest resolution.
  const activeCurrency = currencies.find((c) => c.instrument === params.instrument) ?? currencies[0];
  const periods = activeCurrency?.resolutionPeriods ?? [];
  const activePeriod = periods.find((p) => p.subCategoryCode === params.subCategoryCode) ?? periods[0];

  const instrument = activeCurrency?.instrument;
  const subCategoryCode = activePeriod?.subCategoryCode as SnakeMarketsFindSeriesSubCategoryCode | undefined;

  // Normalise the URL to the resolved selection (so a bare `/snake` lands on a
  // concrete currency+resolution and back/forward stays consistent).
  useEffect(() => {
    if (!instrument || !subCategoryCode) return;
    if (params.instrument !== instrument || params.subCategoryCode !== subCategoryCode) {
      navigate(snakeHref(instrument, subCategoryCode), { replace: true });
    }
  }, [instrument, subCategoryCode, params.instrument, params.subCategoryCode, navigate]);

  const {
    data: series,
    isLoading: seriesLoading,
    refetch,
  } = useSnakeMarketsFindSeries(
    { instrument: instrument!, subCategoryCode: subCategoryCode! },
    // keepPreviousData: switching resolution changes the query key, but we keep
    // showing the previous instance's data until the new one loads so the page
    // never drops into its loading branch (which would unmount the chart). The
    // chart stays alive and only the Target line moves to the new strike.
    { query: { enabled: Boolean(instrument && subCategoryCode), placeholderData: keepPreviousData } },
  );

  // The page does NOT auto-advance (the lobby does). When the window closes we
  // hold the closed state and surface a manual "Go To Live" the user taps to
  // jump to the now-live instance — matching Figma.
  const queryClient = useQueryClient();
  const closed = isClosed(series?.closeTime ?? null, nowMs);

  // At a window boundary the price-to-beat strike isn't known until the prior
  // window settles (a beat after close). While not ready we show a pending state
  // (never a provisional/wrong strike) and refetch until the backend resolves it.
  const priceToBeatReady = series?.priceToBeatReady ?? true;
  useSnakePriceToBeatReady(Boolean(series) && !priceToBeatReady, refetch);

  // Keep positions/history live: fills+sells via the private bus, settlement via the market channel.
  useTradingRealtimeSync();
  useSettlementResync(series?.marketId, series?.externalMarketId);

  // A clicked past pill freezes the page on that settled window (frozen chart +
  // Final Price). null = the live view. Cleared by goLive(). Held in component
  // state (not the URL) — a shareable `?pastMarketId=` link is a future add-on.
  const [selectedPastMarketId, setSelectedPastMarketId] = useState<string | null>(null);
  // Switching currency or resolution navigates (params change) but does NOT
  // remount the page, so the past selection would otherwise linger and keep
  // showing the previous instrument's frozen window. Reset to live whenever the
  // selection key changes — the React "adjust state when a prop changes" pattern
  // (set during render, covers in-app switches AND browser back/forward).
  const selectionKey = `${instrument}/${subCategoryCode}`;
  const [prevSelectionKey, setPrevSelectionKey] = useState(selectionKey);
  if (prevSelectionKey !== selectionKey) {
    setPrevSelectionKey(selectionKey);
    setSelectedPastMarketId(null);
  }
  const { data: pastMarket } = useSnakeMarketsFindPastMarket(selectedPastMarketId ?? '', {
    query: { enabled: Boolean(selectedPastMarketId), placeholderData: keepPreviousData },
  });
  // Only treat the past view as active once its data has loaded, so we never
  // mount the frozen chart without a seed.
  const viewingPast = Boolean(selectedPastMarketId) && Boolean(pastMarket);

  // Order book (shared query with the Order Book section) — its yes/no prices
  // are the load-time fallback for the buy panel. `series.outcome.yesPrice` is
  // often null right after a window opens, which previously left the Up/Down
  // buttons disabled until the first live `PriceUpdated` arrived; the book has
  // usable prices immediately, so the buttons enable on load.
  const priceOutcomeId = series?.outcome?.externalOutcomeId ?? '';
  const { data: orderBook } = usePlaeeOrderBookGetOrderBook(priceOutcomeId, {
    query: { enabled: Boolean(priceOutcomeId) && !viewingPast, staleTime: 5_000 },
  });

  function goLive() {
    setSelectedPastMarketId(null);
    void refetch();
    if (instrument && subCategoryCode) {
      void queryClient.invalidateQueries({
        queryKey: getSnakeMarketsFindSeriesHistoryQueryKey({ instrument, subCategoryCode }),
      });
    }
  }

  function handleCurrency(nextInstrument: string) {
    const next = currencies.find((c) => c.instrument === nextInstrument);
    const nextPeriod = next?.resolutionPeriods[0];
    if (!next || !nextPeriod) return;
    navigate(snakeHref(nextInstrument, nextPeriod.subCategoryCode));
  }

  function handleResolution(nextSubCategoryCode: string) {
    if (!instrument) return;
    navigate(snakeHref(instrument, nextSubCategoryCode));
  }

  if (currenciesLoading) {
    return <SnakeMarketPageSkeleton />;
  }
  if (currencies.length === 0 || !activeCurrency || !instrument || !subCategoryCode) {
    return <p className="py-8 text-center text-sm text-muted">No snake markets available.</p>;
  }

  // Up/Down prices in cents. Per side the fallback order is: live tick →
  // outcome seed → order book (yes/no price, 0–1 dollars → cents). The order
  // book is what enables the buy buttons on first paint, before any live
  // `PriceUpdated` arrives and while the outcome seed is still null.
  const dollarsToCents = (v: number | null | undefined) => (v != null ? Math.round(v * 100) : null);
  let upCents = live.upCents ?? toCents(series?.outcome?.yesPrice ?? null) ?? dollarsToCents(orderBook?.yesPrice);
  let downCents = live.downCents ?? toCents(series?.outcome?.noPrice ?? null) ?? dollarsToCents(orderBook?.noPrice);
  // A binary market's two sides sum to 100¢, and the live book is often
  // one-sided (e.g. only noPrice / bestBid on a fresh window), so derive a
  // missing side from the known one — otherwise that side's button stays
  // disabled even though a real price exists.
  if (upCents == null && downCents != null) upCents = 100 - downCents;
  else if (downCents == null && upCents != null) downCents = 100 - upCents;
  const strike = parsePrice(series?.outcome?.strikePrice ?? null);
  const previous = parsePrice(series?.resolution?.previousResolutionPrice ?? null);
  const basePrice = strike ?? previous ?? 65_080;

  // Frozen past-view values (only meaningful while `viewingPast`).
  const pastStrike = parsePrice(pastMarket?.strikePrice ?? null);
  const pastFinal = parsePrice(pastMarket?.settledPrice ?? null);

  return (
    // Mobile: single column (source order). Desktop (xl+): two-column market
    // layout per Figma (node 6672:168865) — fluid main column + 380px sticky
    // trade rail, with a wider gutter as the viewport allows. The split waits
    // for xl (not lg): at 1024–1280 the app sidebar + the 380px rail leave the
    // main column ~220–300px, which no header/chart layout survives — there
    // the buy panel keeps flowing inline under the chart, as on tablet.
    <div className="flex flex-col gap-5 xl:grid xl:grid-cols-[minmax(0,1fr)_380px] xl:items-start xl:gap-x-8 xl:gap-y-6 2xl:gap-x-[60px]">
      {series && (
        <SnakePriceSubscriber
          marketId={series.marketId}
          externalMarketId={series.externalMarketId}
          instrument={instrument}
          onPrices={setPrices}
          onSpot={setSpot}
          onTradeDot={addTradeDot}
        />
      )}

      <div className="xl:col-span-2">
        <SnakeCurrencyTabs currencies={currencies} activeInstrument={instrument} onChange={handleCurrency} />
      </div>

      {seriesLoading && !series ? (
        <SnakeMarketBodySkeleton />
      ) : series ? (
        <>
          {/* Main column: market state, chart, and recent windows — on desktop
              they merge into ONE bordered card (Figma node 6678:169599). */}
          <div className="flex min-w-0 flex-col gap-5 xl:col-start-1 lg:gap-7 lg:rounded-[12px] lg:border-[1.5px] lg:border-stroke-soft lg:bg-surface lg:p-8 xl:p-10">
            <SnakeMarketHeader
              currency={activeCurrency}
              series={series}
              periods={periods}
              activeSubCategoryCode={subCategoryCode}
              onResolutionChange={handleResolution}
              spot={live.spot}
              nowMs={nowMs}
              closed={closed}
              onGoLive={goLive}
              priceToBeatPending={!priceToBeatReady}
              pastView={
                viewingPast
                  ? {
                      strike: pastStrike,
                      finalPrice: pastFinal,
                      wonSide: pastMarket!.wonSide,
                      openTime: pastMarket!.openTime,
                      closeTime: pastMarket!.closeTime,
                    }
                  : null
              }
            />

            {/* Dollar-space price chart + dashed Target line (self-seeds from the
                first live tick). */}
            {/* TODO(snake-chart-§5.4): add the left-axis executed-trade-price ladder
                and own-buy-in dots (design-pending; the old Up% trade-dot markers
                are dropped). */}
            {/* Live: keyed on the instrument only — the graph is the instrument's
                price stream, identical across resolutions, so switching the
                resolution dropdown keeps the same chart and just moves the Target
                line. Past: keyed on the selected market so it remounts (tearing
                down the live socket) into a frozen, static seed. */}
            {/* Desktop height per Figma: a wide, short chart inside the card. */}
            <div className="relative h-[286px] w-full overflow-hidden rounded-[12px] bg-surface lg:h-[300px]">
              {selectedPastMarketId && !pastMarket ? (
                <Skeleton className="h-full w-full rounded-[12px]" />
              ) : viewingPast ? (
                <LightweightChart
                  key={pastMarket!.marketId}
                  frozen
                  // No socket runs in frozen mode; the symbol only drives axis
                  // label formatting (decimals + tick-size floor).
                  instrumentSymbol={instrument}
                  basePrice={pastStrike ?? basePrice}
                  targetPrice={pastStrike}
                  seedCandles={pastMarket!.candles}
                  finalPrice={pastFinal}
                  finalPriceLabel={formatSpot(pastFinal)}
                />
              ) : (
                <LightweightChart
                  key={instrument}
                  instrumentSymbol={instrument}
                  basePrice={basePrice}
                  targetPrice={strike}
                />
              )}
            </div>

            <SnakeTimelineStrip
              instrument={instrument}
              subCategoryCode={subCategoryCode}
              viewedCloseTime={viewingPast ? pastMarket!.closeTime : series?.closeTime}
              onGoLive={goLive}
              onSelectPast={setSelectedPastMarketId}
            />
          </div>

          {/* Trading entry points — hidden entirely on a settled past window.
              Phones (<md): a docked Up/Down bar above the bottom menu opens a
              drawer with the Set Amount panel. Tablet (md–lg): the panel inline
              in the column flow. Desktop (lg+): the panel in a sticky rail
              beside the chart (per Figma). */}
          {!viewingPast && (
            <>
              <aside className="hidden md:block xl:sticky xl:top-20 xl:col-start-2 xl:row-start-2">
                <SnakeBuyPanel
                  instrument={instrument}
                  subCategoryCode={subCategoryCode}
                  // Question recap on the rail card (desktop only, Figma node
                  // 6672:170285) — icon + title + ET window above the divider.
                  question={{
                    imageUrl: activeCurrency.imageUrl,
                    title: `${activeCurrency.shortName ?? activeCurrency.name ?? activeCurrency.instrument} Up or Down`,
                    window: formatWindowEt(series.openTime, series.closeTime),
                  }}
                  // While the price-to-beat is still resolving, disable buying (empty
                  // outcome) and hide the provisional chance — never trade a wrong strike.
                  outcomeId={priceToBeatReady ? (series.outcome?.externalOutcomeId ?? '') : ''}
                  marketId={series.marketId}
                  upCents={priceToBeatReady ? upCents : null}
                  downCents={priceToBeatReady ? downCents : null}
                />
              </aside>
              <SnakeTradeBar
                instrument={instrument}
                subCategoryCode={subCategoryCode}
                outcomeId={priceToBeatReady ? (series.outcome?.externalOutcomeId ?? '') : ''}
                marketId={series.marketId}
                upCents={priceToBeatReady ? upCents : null}
                downCents={priceToBeatReady ? downCents : null}
              />
            </>
          )}

          {/* Info sections continue the main column under the chart card. On
              phones each section floats as its own card; on desktop they become
              rows of ONE bordered card with hairline dividers (Figma node
              6672:168881) — the row chrome is stripped per section via
              `section-row-style.ts`. */}
          <div className="flex min-w-0 flex-col gap-5 xl:col-start-1 lg:gap-0 lg:rounded-[12px] lg:border-[1.5px] lg:border-stroke-soft lg:bg-surface lg:px-3 lg:py-2">
            <SnakeOpenPositions
              outcomeId={series.outcome?.externalOutcomeId ?? ''}
              upCents={priceToBeatReady ? upCents : null}
              downCents={priceToBeatReady ? downCents : null}
            />

            <SnakeMyHistory outcomeId={series.outcome?.externalOutcomeId ?? ''} />

            <SnakeSections series={series} />
          </div>
        </>
      ) : (
        <p className="py-8 text-center text-sm text-muted xl:col-span-2">No live market for this selection right now.</p>
      )}
    </div>
  );
}
